@import "./components/reset.css";
@import "./components/container.css";
@import "./components/surface.css";
@import "./components/media.css";
@import "./components/controls-backdrop.css";
@import "./components/buffering.css";
@import "./components/dialog.css";
@import "./components/controls.css";
@import "./components/time.css";
@import "./components/button.css";
@import "./components/button-group.css";
@import "./components/badge.css";
@import "./components/icons.css";
@import "./components/poster.css";
@import "./components/thumbnail.css";
@import "./components/slider.css";
@import "./components/popup.css";
@import "./components/menus.css";
@import "./components/captions.css";
@import "./components/input-indicator.css";
@import "./components/volume-indicator.css";
@import "./components/status-indicator.css";
@import "./components/seek-indicator.css";
@import "../../shared/css/video/icon-state.css";

/* Video-specific container styles */

.media-default-skin--video {
  --media-default-accent-color: oklch(1 0 0);
  --media-border-color: light-dark(oklch(0 0 0 / 0.1), oklch(1 0 0 / 0.15));
  --media-focus-ring-color: light-dark(oklch(0 0 0), oklch(1 0 0));
  --media-video-border-radius: var(--media-container-border-radius);

  --media-surface-background-color: oklch(1 0 0 / 0.1);
  --media-surface-inner-border-color: oklch(1 0 0 / 0.1);
  --media-surface-outer-border-color: oklch(0 0 0 / 0.1);
  --media-surface-shadow-color: oklch(0 0 0 / 0.15);
  --media-surface-backdrop-filter: blur(16px) saturate(1.5);

  --media-controls-transition-duration: 100ms;
  --media-controls-transition-timing-function: ease-out;

  --media-dialog-transition-duration: 350ms;
  --media-dialog-transition-delay: 100ms;
  --media-dialog-transition-timing-function: ease-out;

  --media-popup-transition-duration: 100ms;
  --media-popup-transition-timing-function: ease-out;

  overflow: clip;
  background: oklch(0 0 0);

  @media (prefers-reduced-motion: reduce) {
    --media-dialog-transition-duration: 50ms;
    --media-dialog-transition-delay: 0ms;
    --media-popup-transition-duration: 0ms;

    .media-dialog__popup {
      scale: 1;
      transition-property: opacity;
    }
  }

  @media (prefers-reduced-transparency: reduce) or (prefers-contrast: more) {
    --media-surface-background-color: oklch(0 0 0);
    --media-surface-inner-border-color: oklch(1 0 0 / 0.25);
    --media-surface-outer-border-color: transparent;
  }

  &:has(.media-controls--root:not([data-visible])) {
    /* Slight delay to hide controls on non-touch devices after interaction */
    @media (pointer: fine) {
      --media-controls-transition-duration: 300ms;
    }
    @media (pointer: coarse) {
      --media-controls-transition-duration: 150ms;
    }
    @media (prefers-reduced-motion: reduce) {
      --media-controls-transition-duration: 50ms;
    }
  }

  /* Inner border ring */
  &::after {
    position: absolute;
    inset: 0;
    z-index: 10;
    pointer-events: none;
    content: "";
    border-radius: inherit;
    box-shadow: inset 0 0 0 1px var(--media-border-color);
  }

  &:fullscreen {
    --media-container-border-radius: 0;

    &::after {
      display: none;
    }

    @media (width >= 1280px) {
      --media-scale: 1.25;
    }
    @media (width >= 1536px) {
      --media-scale: 1.5;
    }
    @media (width >= 1920px) {
      --media-scale: 1.75;
    }
  }

  * {
    --media-focus-ring-color: oklch(1 0 0);
  }
}

/* Dialog  */

.media-default-skin--video .media-dialog__popup {
  position: absolute;
  top: 50%;
  left: 50%;
  z-index: 20;
  display: flex;
  flex-direction: column;
  gap: calc(var(--media-spacing) * 3);
  width: 100%;
  max-width: calc(var(--media-spacing) * 72);
  padding: calc(var(--media-spacing) * 3);
  color: oklch(1 0 0);
  text-shadow: 0 1px 0 oklch(0 0 0 / 0.25);
  border-radius: calc(var(--media-spacing) * 7);
  translate: -50% -50%;
  transition-delay: var(--media-dialog-transition-delay);
  transition-timing-function: var(--media-dialog-transition-timing-function);
  transition-duration: var(--media-dialog-transition-duration);
  transition-property: opacity, scale;
}

.media-default-skin--video .media-dialog__popup[data-starting-style],
.media-default-skin--video .media-dialog__popup[data-ending-style],
.media-default-skin--video media-error-dialog[data-starting-style] .media-dialog__popup,
.media-default-skin--video media-error-dialog[data-ending-style] .media-dialog__popup {
  opacity: 0;
  scale: 0.95;
}
.media-default-skin--video .media-dialog__popup[data-ending-style],
.media-default-skin--video media-error-dialog[data-ending-style] .media-dialog__popup {
  transition-delay: 0ms;
}

.media-default-skin--video .media-dialog__content {
  display: flex;
  flex-direction: column;
  gap: calc(var(--media-spacing) * 2);
  padding: calc(var(--media-spacing) * 2) calc(var(--media-spacing) * 2) calc(var(--media-spacing) * 1.5);
  text-shadow: inherit;
}

.media-default-skin--video .media-dialog__title {
  font-size: var(--media-font-size-medium);
}

.media-default-skin--video .media-slider__value {
  text-shadow: 0 1px 0 var(--media-shadow-current-color);
}

/* Controls (hide/show behavior) */
.media-default-skin--video .media-controls--root {
  --media-inset-factor: 2;
  --media-inset: calc(var(--media-spacing) * var(--media-inset-factor));
  --media-base-boundary-offset: var(--media-inset-factor);

  z-index: 10;
  display: contents;
  color: oklch(1 0 0);
  transition-timing-function: var(--media-controls-transition-timing-function);
  /* Speed up the entry transition */
  transition-duration: calc(var(--media-controls-transition-duration) / 2);

  .media-controls--primary,
  .media-controls--secondary {
    position: absolute;
  }
  /* Primary must be higher for the preview/thumbnail */
  .media-controls--primary {
    z-index: 20;
  }
  .media-controls--secondary {
    z-index: 10;
  }

  .media-controls--primary {
    inset-inline: var(--media-inset);
    bottom: var(--media-inset);
    transform-origin: bottom;
  }

  .media-controls--secondary {
    top: var(--media-inset);
    right: var(--media-inset);
    container-type: normal;
    transform-origin: top;
  }

  .media-time-controls {
    flex: 1;
    padding-inline: calc(var(--media-spacing) * 2);
  }

  @container media-root (width < 32rem) {
    .media-controls--primary,
    .media-controls--secondary {
      transition-timing-function: inherit;
      transition-duration: inherit;

      @media (pointer: fine) {
        transition-property: filter, opacity, scale, translate;
      }

      @media (pointer: coarse) {
        transition-property: opacity, scale, translate;
      }
    }

    &:after {
      display: none;
    }

    &:not([data-visible]) {
      .media-controls--primary,
      .media-controls--secondary {
        pointer-events: none;
        opacity: 0;
        scale: 0.95;
        transition-duration: var(--media-controls-transition-duration);

        @media (pointer: fine) and (prefers-reduced-motion: no-preference) {
          filter: blur(8px);
        }

        @media (prefers-reduced-motion: reduce) {
          scale: 1;
        }
      }

      .media-controls--primary {
        @media (prefers-reduced-motion: no-preference) {
          translate: 0 4px;
        }
      }

      .media-controls--secondary {
        @media (prefers-reduced-motion: no-preference) {
          translate: 0 -4px;
        }
      }
    }

    .media-button--captions {
      display: none;
    }
  }

  @container media-root (width >= 32rem) {
    position: absolute;
    inset-inline: var(--media-inset);
    bottom: var(--media-inset);
    display: flex;
    transform-origin: bottom;

    .media-controls--primary,
    .media-controls--secondary {
      display: contents;

      &::after {
        display: none;
      }
    }

    &:not([data-visible]) {
      pointer-events: none;
      opacity: 0;
      scale: 0.95;
      transition-duration: var(--media-controls-transition-duration);

      @media (pointer: fine) and (prefers-reduced-motion: no-preference) {
        filter: blur(8px);
      }

      @media (prefers-reduced-motion: reduce) {
        scale: 1;
      }

      @media (prefers-reduced-motion: no-preference) {
        translate: 0 4px;
      }
    }

    .media-time-controls {
      padding-inline: calc(var(--media-spacing) * 3);
    }
  }

  @media (pointer: fine) {
    transition-property: filter, opacity, scale, translate;
  }

  @media (pointer: coarse) {
    transition-property: opacity, scale, translate;
  }

  @container media-root (width > 42rem) {
    --media-inset-factor: 3;
  }
}

/* Hide cursor when controls are hidden */
.media-default-skin--video:has(.media-controls--root:not([data-visible])) {
  cursor: none;
}

/* Sliders */

.media-default-skin--video .media-slider__track {
  background-color: oklch(1 0 0 / 0.2);
}
